import { el, clear } from "./dom.js";
import { MAG, magGlass } from "./magazine/kit.js";
import { magField } from "./magazine/detail.js";
import { updateMediaItem, loadTagFacets } from "../db/media.js";
import { normalizeTag, addTag, removeTag, REVIEW_MAX_LENGTH } from "../utils/tags.js";
import { showToast } from "./toast.js";

// Roadmap #34 + #33: a longer review beside the short note, and your own tags
// ("cozy", "rainy day", "with friends"…) — on every detail page, saved through
// the item's own update (so History and sync see it), synced in media_extra.

export function reviewField(mediaType, item, { index = 6 } = {}) {
	const counter = el("div", {
		style: {
			fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "8.5px",
			letterSpacing: ".06em", textTransform: "uppercase", color: MAG.faint, textAlign: "right", marginTop: "7px",
		},
	}, `${(item.review ?? "").length} / ${REVIEW_MAX_LENGTH}`);
	let saved = item.review ?? "";
	const ta = el("textarea", {
		value: saved,
		maxlength: String(REVIEW_MAX_LENGTH),
		rows: saved ? 6 : 2,
		placeholder: "Your review — as long as you like. Only you see it.",
		oninput: (e) => {
			counter.textContent = `${e.currentTarget.value.length} / ${REVIEW_MAX_LENGTH}`;
		},
		onfocus: (e) => {
			if (e.currentTarget.rows < 6) e.currentTarget.rows = 6;
		},
		onblur: async (e) => {
			const v = e.currentTarget.value.trim();
			if (v === saved.trim()) return;
			saved = v;
			try {
				await updateMediaItem(mediaType, item.id, { review: v || null });
			} catch (err) {
				showToast(`Couldn't save the review: ${err?.message ?? err}`);
			}
		},
		style: {
			width: "100%", resize: "vertical", background: "transparent", border: "0", outline: "none",
			fontFamily: "'Archivo', sans-serif", fontWeight: "500", fontSize: "13.5px", color: "#eaeaf0", lineHeight: "1.55",
		},
	});
	return magField("Review", magGlass([ta, counter], { style: { padding: "12px 14px" } }), { index });
}

export function tagsField(mediaType, item, { index = 3, suggestions = [] } = {}) {
	let tags = [...(item.tags ?? [])];
	const chips = el("div", { style: { display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" } });
	const input = el("input", {
		type: "text",
		placeholder: tags.length ? "+ tag" : "+ Add a tag (cozy, with friends…)",
		maxlength: "32",
		autocomplete: "off",
		list: `tag-suggest-${item.id}`,
		style: {
			flex: "1", minWidth: "120px", background: "transparent", border: "0", outline: "none", padding: "6px 2px",
			fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "12.5px", color: MAG.ink,
		},
	});
	const datalist = el("datalist", { id: `tag-suggest-${item.id}` });

	async function save(next) {
		tags = next;
		paint();
		try {
			await updateMediaItem(mediaType, item.id, { tags: next });
			await loadTagFacets();
		} catch (err) {
			showToast(`Couldn't save tags: ${err?.message ?? err}`);
		}
	}

	function commitInput() {
		const t = normalizeTag(input.value);
		input.value = "";
		if (t && !tags.includes(t)) save(addTag(tags, t));
	}

	input.addEventListener("keydown", (e) => {
		if (e.key === "Enter" || e.key === ",") {
			e.preventDefault();
			commitInput();
		} else if (e.key === "Backspace" && !input.value && tags.length) {
			save(tags.slice(0, -1));
		}
	});
	input.addEventListener("change", commitInput);

	function paint() {
		clear(chips);
		for (const t of tags) {
			chips.append(el("button", {
				type: "button",
				"aria-label": `Remove tag ${t}`,
				onclick: () => save(removeTag(tags, t)),
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "11px", cursor: "pointer",
					borderRadius: "999px", padding: "6px 10px", border: "0", background: MAG.lime, color: "#0b0b0f",
				},
			}, `#${t} ×`));
		}
		chips.append(input);
		clear(datalist);
		for (const s of suggestions) if (!tags.includes(s)) datalist.append(el("option", { value: s }));
		input.placeholder = tags.length ? "+ tag" : "+ Add a tag (cozy, with friends…)";
	}
	paint();
	return magField("Tags", magGlass([chips, datalist], { style: { padding: "8px 12px" } }), { index });
}
